import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { PageFrame } from '../components/work/page-chrome';
import WorkItemTableRow from '../components/work/WorkItemTableRow';
import FilterBar from '../components/work/FilterBar';
import Peek from '../components/work/Peek';
import BulkBar, { type BulkSelection } from '../components/work/BulkBar';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { BrandEmpty, LoadingRows, PageTitle, WorkPageError } from '../components/work/page-states';
import WorkKanban from '../components/work/WorkKanban';
import WorkListPagination from '../components/work/WorkListPagination';
import {
  DueDateCell,
  PriorityLabel,
  ProjectNameCell,
  StateLabel,
  WorkItemTitleLink,
  WorkKeyCell,
} from '../components/work/table-cells';
import { useOverlays } from '../components/work/overlay-context';
import { ApiError, apiFetch, isProjectMembershipError, v1Path } from '../lib/api';
import {
  filtersToSearchParams,
  searchParamsToFilters,
  type DisplaySpec,
  type FilterSpec,
} from '../lib/filters';
import { fetchStatesByProjectIds } from '../lib/project-states';
import { paginateItems, WORK_LIST_PAGE_SIZE } from '../lib/work-item-list';
import { defaultWorkListOrderBy } from '../lib/work-list-sort';
import {
  workItemSubtypeLabel,
  type ProjectDto,
  type WorkItemSubtypeDefinition,
} from '../lib/work-types';
import {
  columnItemsForState,
  neighboursForDrop,
  orderedItemKeys,
  previousAfter,
  previousBefore,
  sortItemsByColumn,
} from '../components/work/kanban-rank';

type WorkItemSlim = {
  id: string;
  key: string;
  title: string;
  state_id: string;
  priority: string;
  project_id: string;
  assignee_ids: string[];
  label_ids: string[];
  sort_order: number;
  version: number;
  start_date: string | null;
  due_date: string | null;
  type?: string;
  subtype?: string | null;
  blocked_reason?: string | null;
};

type StateDto = {
  id: string;
  name: string;
  group: string;
  color: string;
  position?: number;
  is_default: boolean;
};

type ViewDto = {
  id: string;
  name: string;
  layout: 'list' | 'kanban';
  filters: FilterSpec;
  display: DisplaySpec;
  is_system: boolean;
  is_shared: boolean;
};

type ListResponse = {
  items: WorkItemSlim[];
  page: { limit: number; offset: number; total: number };
};

type MoveBody = {
  version: number;
  state_id: string;
  before_id?: string;
  after_id?: string;
};

function mergeSpecs(
  storedFilters: FilterSpec,
  storedDisplay: DisplaySpec,
  search: URLSearchParams,
): { filters: FilterSpec; display: DisplaySpec } {
  const parsed = searchParamsToFilters(search);
  return {
    filters: { ...storedFilters, ...parsed.filters },
    display: { ...storedDisplay, ...parsed.display },
  };
}

function sortStatesByPosition(states: StateDto[]): StateDto[] {
  return [...states].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name));
}

export default function ViewPage() {
  const { projectId, viewId } = useParams<{ projectId: string; viewId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isWorkspace = !projectId;
  const [view, setView] = useState<ViewDto | null>(null);
  const [items, setItems] = useState<WorkItemSlim[]>([]);
  const [states, setStates] = useState<StateDto[]>([]);
  const [projectNames, setProjectNames] = useState<Map<string, string>>(new Map());
  const [subtypesByProject, setSubtypesByProject] = useState<Map<string, WorkItemSubtypeDefinition[]>>(
    new Map(),
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast, openCreateWorkItem } = useOverlays();
  const addCard = (stateId: string) => openCreateWorkItem({ projectId, stateId });
  const [selection, setSelection] = useState<BulkSelection>(new Map());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(WORK_LIST_PAGE_SIZE);

  const merged = useMemo(() => {
    if (!view) return { filters: {} as FilterSpec, display: { layout: 'list' } as DisplaySpec };
    return mergeSpecs(view.filters, view.display, searchParams);
  }, [view, searchParams]);

  const orderBy = defaultWorkListOrderBy(merged.display.layout, merged.display.order_by);
  const canReorder = Boolean(projectId) && view?.layout === 'kanban' && orderBy === 'sort_order';
  const showEmptyGroups = merged.display.show_empty_groups !== false;
  const listQueryKey = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('peek');
    return params.toString();
  }, [searchParams]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!viewId) return;
      if (!opts?.silent) setLoading(true);
      setError(null);
      try {
        const viewPath = projectId
          ? `/projects/${projectId}/views/${viewId}`
          : `/views/${viewId}`;
        const loaded = await apiFetch<ViewDto>(v1Path(viewPath));
        setView(loaded);
        const params = new URLSearchParams(searchParams);
        params.delete('peek');
        params.set('limit', '100');
        params.set('offset', '0');
        const qs = params.toString();
        const listPromise = apiFetch<ListResponse>(`${v1Path(`${viewPath}/execute`)}?${qs}`);
        if (projectId) {
          const [list, stateRes] = await Promise.all([
            listPromise,
            apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)),
          ]);
          setItems(list.items);
          setTotal(list.page.total);
          setStates(stateRes.items);
          setProjectNames(new Map());
          setSubtypesByProject(new Map());
        } else {
          const [list, projectRes] = await Promise.all([
            listPromise,
            apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
          ]);
          const loadedStates = await fetchStatesByProjectIds(
            Array.from(new Set(list.items.map((item) => item.project_id))),
          );
          setItems(list.items);
          setTotal(list.page.total);
          setStates(Array.from(loadedStates.byId.values()));
          setProjectNames(new Map(projectRes.items.map((project) => [project.id, project.name])));
          setSubtypesByProject(
            new Map(projectRes.items.map((project) => [project.id, project.subtypes ?? []])),
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load view');
        setItems([]);
        setTotal(0);
        setStates([]);
        setProjectNames(new Map());
        setSubtypesByProject(new Map());
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, viewId, listQueryKey],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const workspaceSubtypeOptions = useMemo(() => {
    const namesByKey = new Map<string, Set<string>>();
    for (const definitions of Array.from(subtypesByProject.values())) {
      for (const subtype of definitions) {
        const names = namesByKey.get(subtype.key) ?? new Set<string>();
        names.add(subtype.name);
        namesByKey.set(subtype.key, names);
      }
    }
    return Array.from(namesByKey, ([key, names]) => ({
      key,
      name: Array.from(names).join(' / '),
    })).sort((left, right) => left.name.localeCompare(right.name));
  }, [subtypesByProject]);

  function applyFilters(next: { filters: FilterSpec; display: DisplaySpec }) {
    const peek = searchParams.get('peek') ?? undefined;
    const params = filtersToSearchParams(next.filters, next.display, { peek });
    setSearchParams(params, { replace: true });
  }

  function resetOverrides() {
    const peek = searchParams.get('peek');
    const params = new URLSearchParams();
    if (peek) params.set('peek', peek);
    setSearchParams(params, { replace: true });
  }

  async function save() {
    if (!viewId || !view) return;
    if (view.is_system) {
      setError('System views cannot be saved.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const path = projectId
        ? `/projects/${projectId}/views/${viewId}`
        : `/views/${viewId}`;
      const updated = await apiFetch<ViewDto>(v1Path(path), {
        method: 'PATCH',
        body: JSON.stringify({
          name: view.name,
          layout: view.layout,
          filters: merged.filters,
          display: {
            ...view.display,
            ...merged.display,
            layout: view.layout,
          },
          is_shared: view.is_shared,
        }),
      });
      setView(updated);
      // Stored spec is source of truth — clear URL filter/display overrides (Reset-equivalent).
      resetOverrides();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save view');
      }
    } finally {
      setSaving(false);
    }
  }

  async function commitMove(item: WorkItemSlim, destStateId: string, insertBeforeId: string | null) {
    if (!projectId) return;
    if (!canReorder) {
      toast('Reorder is only allowed when Order is Manual.');
      return;
    }
    if (item.id === insertBeforeId && item.state_id === destStateId) return;

    const destColumn = columnItemsForState(items, destStateId);
    const neighbours = neighboursForDrop(destColumn, item.id, insertBeforeId);
    const samePlace =
      item.state_id === destStateId &&
      (neighbours.before_id ?? null) === previousBefore(destColumn, item.id) &&
      (neighbours.after_id ?? null) === previousAfter(destColumn, item.id);
    if (samePlace) return;

    const snapshot = items;
    const optimistic: WorkItemSlim = { ...item, state_id: destStateId };
    setItems((curr) => curr.map((row) => (row.id === item.id ? optimistic : row)));

    const body: MoveBody = {
      version: item.version,
      state_id: destStateId,
      ...neighbours,
    };

    try {
      const dto = await apiFetch<WorkItemSlim>(
        v1Path(`/projects/${projectId}/work-items/${item.id}/move`),
        { method: 'POST', body: JSON.stringify(body) },
      );
      setItems((curr) =>
        sortItemsByColumn(curr.map((row) => (row.id === dto.id ? { ...row, ...dto } : row))),
      );
      await load({ silent: true });
    } catch (err) {
      setItems(snapshot);
      if (err instanceof ApiError && err.status === 409) {
        toast('Someone else updated this card. Reloading.');
        await load({ silent: true });
        return;
      }
      toast(err instanceof Error ? err.message : 'Move failed');
    }
  }

  const isKanban = Boolean(projectId) && view?.layout === 'kanban';
  const stateById = useMemo(() => {
    const map = new Map<string, StateDto>();
    for (const state of states) map.set(state.id, state);
    return map;
  }, [states]);
  const paged = useMemo(() => paginateItems(items, page, pageSize), [items, page, pageSize]);
  const peekKeys = useMemo(
    () =>
      isKanban
        ? orderedItemKeys(sortStatesByPosition(states), items)
        : items.map((item) => item.key),
    [isKanban, states, items],
  );

  useEffect(() => {
    setPage(1);
  }, [listQueryKey, pageSize]);

  if (!viewId) {
    return <div className="p-6 text-sm text-muted-foreground">Missing view.</div>;
  }

  const listRows = paged.slice;
  const selectedOnPage = listRows.filter((item) => selection.has(item.id)).length;
  const allOnPageSelected = listRows.length > 0 && selectedOnPage === listRows.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
        <PageTitle
          title={view?.name ?? 'View'}
          count={loading || error ? undefined : total}
          action={
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={resetOverrides}>
                Reset
              </Button>
              {view && !view.is_system ? (
                <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              ) : null}
            </div>
          }
        />

        {view && !isProjectMembershipError(error) ? (
          <>
            <FilterBar
              projectId={projectId}
              subtypeOptions={isWorkspace ? workspaceSubtypeOptions : undefined}
              filters={merged.filters}
              display={{ ...merged.display, layout: view.layout }}
              onChange={applyFilters}
              onClear={resetOverrides}
            />
          </>
        ) : null}

        <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />

        {error ? null : isKanban && !canReorder ? (
          <Alert>
            <AlertTitle>Dragging disabled</AlertTitle>
            <AlertDescription>Switch Sort to Manual to rearrange cards.</AlertDescription>
          </Alert>
        ) : null}

        {error ? null : loading ? (
          <LoadingRows />
        ) : isKanban ? (
          <WorkKanban
            projectId={projectId ?? ''}
            columns={sortStatesByPosition(states)}
            items={items}
            showEmptyGroups={showEmptyGroups}
            canReorder={canReorder}
            subtypeLabelForItem={
              isWorkspace
                ? (item) =>
                    workItemSubtypeLabel(item.subtype, subtypesByProject.get(item.project_id))
                : undefined
            }
            onMove={(item, destStateId, insertBeforeId) => {
              void commitMove(item, destStateId, insertBeforeId);
            }}
            onAddCard={addCard}
          />
        ) : total === 0 ? (
          <BrandEmpty title="Nothing in this view." />
        ) : (
          <>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                {isWorkspace ? null : (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false
                      }
                      onCheckedChange={(value) => {
                        if (value) {
                        setSelection(
                          new Map(
                            listRows.map((item) => [item.id, { id: item.id, version: item.version }]),
                          ),
                        );
                        } else {
                          setSelection(new Map());
                        }
                      }}
                      aria-label={allOnPageSelected ? 'Deselect all' : 'Select all'}
                    />
                  </TableHead>
                )}
                <TableHead className="w-[7.5rem]">Key</TableHead>
                <TableHead className="min-w-0 w-[32%]">Title</TableHead>
                {isWorkspace ? (
                  <TableHead className="hidden w-[11rem] md:table-cell">Stream</TableHead>
                ) : null}
                <TableHead className="hidden w-28 md:table-cell">Priority</TableHead>
                <TableHead className="hidden w-32 md:table-cell">Due date</TableHead>
                <TableHead className="hidden w-[9rem] md:table-cell">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence initial={false}>
              {listRows.map((item) => {
                const state = stateById.get(item.state_id);
                return (
                <WorkItemTableRow key={item.id} workItemId={item.id}>
                  {isWorkspace ? null : (
                    <TableCell>
                      <Checkbox
                        checked={selection.has(item.id)}
                        onCheckedChange={(value) => {
                          setSelection((curr) => {
                            const next = new Map(curr);
                            if (value) next.set(item.id, { id: item.id, version: item.version });
                            else next.delete(item.id);
                            return next;
                          });
                        }}
                        aria-label={`Select ${item.key}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <WorkKeyCell
                      workItemId={item.id}
                      workItemKey={item.key}
                      projectId={item.project_id}
                    />
                  </TableCell>
                  <TableCell className="min-w-0">
                    <div className="truncate" title={item.title}>
                    <WorkItemTitleLink
                      workItemId={item.id}
                      workItemKey={item.key}
                      projectId={item.project_id}
                      title={item.title}
                    />
                    </div>
                    <span className="mt-0.5 flex items-center gap-2 md:hidden">
                      <PriorityLabel priority={item.priority} />
                      {state ? <StateLabel name={state.name} color={state.color} group={state.group} blocked={Boolean(item.blocked_reason)} /> : null}
                    </span>
                  </TableCell>
                  {isWorkspace ? (
                    <TableCell className="hidden min-w-0 md:table-cell">
                      <ProjectNameCell
                        name={projectNames.get(item.project_id)}
                        workItemKey={item.key}
                        projectId={item.project_id}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="hidden md:table-cell">
                    <PriorityLabel priority={item.priority} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <DueDateCell value={item.due_date} done={state?.group === 'completed' || state?.group === 'cancelled'} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {state ? <StateLabel name={state.name} color={state.color} group={state.group} blocked={Boolean(item.blocked_reason)} /> : null}
                  </TableCell>
                </WorkItemTableRow>
                );
              })}
              </AnimatePresence>
            </TableBody>
          </Table>
          <WorkListPagination
            page={paged.page}
            pages={paged.pages}
            pageSize={pageSize}
            shown={paged.slice.length}
            total={items.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
          </>
        )}

        {projectId ? (
          <BulkBar
            projectId={projectId}
            selection={selection}
            onClear={() => setSelection(new Map())}
            onApplied={() => void load({ silent: true })}
          />
        ) : null}
        <Peek projectId={projectId} itemKeys={peekKeys} />

      </div>
    </PageFrame>
  );
}
